import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Table, Space, Select, DatePicker, Input, Button, Tag, Popconfirm, message, Modal, Typography } from 'antd';
import { SearchOutlined, ReloadOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { matriculaService, parcelaService } from '../services/api';
import { handleApiError } from '../utils/apiError';
import { usePermissaoRecurso } from '../hooks/usePermissoes';

const statusOptions = [
  { label: 'Todas', value: null },
  { label: 'Aberta', value: 'ABERTA' },
  { label: 'Paga', value: 'PAGA' },
  { label: 'Cancelada', value: 'CANCELADA' },
];

const defaultFilters = () => ({
  parcelaId: '',
  status: null,
  mesAno: null,
  busca: '',
});

const ParcelasList = () => {
  const { podeExcluir } = usePermissaoRecurso('matriculas');
  const [parcelas, setParcelas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => defaultFilters());
  const [appliedFilters, setAppliedFilters] = useState(() => defaultFilters());
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [bulkDeletingMatriculaId, setBulkDeletingMatriculaId] = useState(null);

  const buildParams = useMemo(() => {
    const params = {};
    if (appliedFilters.parcelaId?.toString().trim()) {
      params.id = appliedFilters.parcelaId.toString().trim();
    }
    if (appliedFilters.status) {
      params.status = appliedFilters.status;
    }
    if (appliedFilters.mesAno) {
      params.mes = appliedFilters.mesAno.month() + 1;
      params.ano = appliedFilters.mesAno.year();
    }
    if (appliedFilters.busca?.trim()) {
      params.q = appliedFilters.busca.trim();
    }
    params.page = pagination.current;
    params.page_size = pagination.pageSize;
    return params;
  }, [appliedFilters, pagination.current, pagination.pageSize]);

  const fetchParcelas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await parcelaService.list(buildParams);
      const payload = response?.data || {};
      const items = Array.isArray(payload.items) ? payload.items : [];
      const apiPagination = payload.pagination || {};

      setParcelas(items);
      setPagination((prev) => ({
        ...prev,
        current: apiPagination.page || prev.current,
        pageSize: apiPagination.page_size || prev.pageSize,
        total: apiPagination.total || 0,
      }));
    } catch (error) {
      handleApiError(error, 'Erro ao carregar parcelas');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchParcelas();
  }, [fetchParcelas]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleResetFilters = () => {
    const reset = defaultFilters();
    setFilters(reset);
    setAppliedFilters(reset);
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const formatCurrency = (valor) => {
    if (valor === null || valor === undefined) return '-';
    const numeric = Number(valor);
    if (Number.isNaN(numeric)) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(numeric);
  };

  const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '—');

  const showSkippedPastParcelasModal = (payload) => {
    const count = payload?.skipped_past_parcelas_count || 0;
    if (!count) return;
    const lista = payload.skipped_past_parcelas || [];
    const resumo = lista.slice(0, 5);
    const restante = lista.length - resumo.length;
    const mensagem = payload.user_message || 'Parcelas vencidas não foram geradas automaticamente.';

    Modal.info({
      title: 'Parcelas vencidas não geradas',
      width: 520,
      content: (
        <div>
          <Typography.Paragraph>{mensagem}</Typography.Paragraph>
          {resumo.length > 0 && (
            <ul style={{ paddingLeft: 20 }}>
              {resumo.map((item) => (
                <li key={`${item.numero}-${item.data_vencimento}`}>
                  Parcela #{item.numero} — venc. {formatDate(item.data_vencimento)} — valor {formatCurrency(item.valor)}
                </li>
              ))}
            </ul>
          )}
          {restante > 0 && (
            <Typography.Text type="secondary">
              +{restante} parcela(s) adicionais não foram geradas
            </Typography.Text>
          )}
        </div>
      ),
    });
  };

  const statusTagColor = (status) => {
    const normalized = (status || '').toUpperCase();
    if (normalized === 'PAGA') return 'green';
    if (normalized === 'CANCELADA') return 'red';
    return 'blue';
  };

  const handleDelete = async (parcelaId) => {
    try {
      await parcelaService.delete(parcelaId);
      message.success('Parcela removida com sucesso');
      fetchParcelas();
    } catch (error) {
      handleApiError(error, 'Erro ao remover parcela');
    }
  };

  const handleBulkDeleteAbertas = async (matriculaId) => {
    setBulkDeletingMatriculaId(matriculaId);
    try {
      const response = await matriculaService.deleteParcelasBulk(matriculaId);
      message.success('Parcelas abertas removidas com sucesso');
      showSkippedPastParcelasModal(response?.data || {});
      fetchParcelas();
    } catch (error) {
      handleApiError(error, 'Erro ao remover parcelas abertas da matrícula');
    } finally {
      setBulkDeletingMatriculaId(null);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Matrícula',
      dataIndex: 'id_matricula',
      key: 'id_matricula',
      width: 110,
    },
    {
      title: 'Aluno',
      dataIndex: 'aluno_nome',
      key: 'aluno_nome',
      render: (text, record) => text || `Matrícula #${record.id_matricula}`,
    },
    {
      title: 'Turma',
      dataIndex: 'turma_nome',
      key: 'turma_nome',
      render: (text) => text || '—',
      width: 180,
    },
    {
      title: 'Responsável',
      dataIndex: 'responsavel_nome',
      key: 'responsavel_nome',
      render: (text) => text || '—',
      width: 220,
    },
    {
      title: 'Vencimento',
      dataIndex: 'data_vencimento',
      key: 'data_vencimento',
      render: (date) => formatDate(date),
      width: 140,
    },
    {
      title: 'Valor',
      dataIndex: 'valor',
      key: 'valor',
      render: (valor) => formatCurrency(valor),
      width: 130,
    },
    {
      title: 'Valor com desconto',
      key: 'valor_com_desconto',
      render: (_, record) => {
        const desconto = Number(record.desconto_percentual || 0);
        if (Number.isFinite(desconto) && desconto > 0) {
          return formatCurrency(record.valor_com_desconto);
        }
        return formatCurrency(record.valor);
      },
      width: 170,
    },
    {
      title: 'Valor pago',
      key: 'valor_pago',
      render: (_, record) => {
        const isPaga = (record.status || '').toUpperCase() === 'PAGA';
        if (!isPaga || record.valor_pago === null || record.valor_pago === undefined) {
          return '—';
        }
        return formatCurrency(record.valor_pago);
      },
      width: 130,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (status) => <Tag color={statusTagColor(status)}>{status || '—'}</Tag>,
      filters: statusOptions
        .filter((option) => option.value)
        .map((option) => ({ text: option.label, value: option.value })),
      onFilter: (value, record) => (record.status || '').toUpperCase() === value,
    },
    {
      title: 'Pagamento',
      dataIndex: 'data_pagamento',
      key: 'data_pagamento',
      render: (date) => formatDate(date),
      width: 140,
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 220,
      render: (_, record) => {
        const status = (record.status || '').toUpperCase();
        const hasFatura = !!record.id_fatura;
        const podeExcluirParcela = (status === 'ABERTA' && !hasFatura) || (status === 'CANCELADA' && !hasFatura);
        const baseDisabled = !podeExcluir || loading;
        const singleDisabled = baseDisabled || bulkDeletingMatriculaId !== null || !podeExcluirParcela;
        const bulkLoading = bulkDeletingMatriculaId === record.id_matricula;
        const bulkDisabled = baseDisabled || bulkDeletingMatriculaId !== null;
        return (
          <Space direction="vertical" size={8}>
            <Popconfirm
              title="Apagar parcela"
              description="Esta ação remove apenas o registro local da parcela."
              okText="Apagar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(record.id)}
              disabled={singleDisabled}
            >
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                disabled={singleDisabled}
              >
                Apagar
              </Button>
            </Popconfirm>
            <Popconfirm
              title="Apagar abertas da matrícula"
              description="As parcelas em aberto serão removidas localmente."
              okText="Apagar abertas"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleBulkDeleteAbertas(record.id_matricula)}
              disabled={bulkDisabled}
            >
              <Button
                size="small"
                danger
                loading={bulkLoading}
                disabled={bulkDisabled}
              >
                Apagar abertas da matrícula
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const onTableChange = (newPagination) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination?.current || 1,
      pageSize: newPagination?.pageSize || prev.pageSize,
    }));
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card title="Filtros" extra={
        <Space>
          <Button icon={<SearchOutlined />} type="primary" onClick={handleApplyFilters}>
            Buscar
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleResetFilters}>
            Limpar
          </Button>
        </Space>
      }>
        <Space size="middle" wrap style={{ width: '100%' }}>
          <div style={{ minWidth: 200 }}>
            <div style={{ marginBottom: 4 }}>ID da parcela</div>
            <Input
              placeholder="Ex.: 123"
              value={filters.parcelaId}
              allowClear
              onChange={(e) => handleFilterChange('parcelaId', (e.target.value || '').replace(/\D/g, ''))}
              onPressEnter={handleApplyFilters}
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <div style={{ marginBottom: 4 }}>Status</div>
            <Select
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              allowClear
              placeholder="Todos"
              options={statusOptions.map(({ label, value }) => ({ label, value }))}
            />
          </div>
          <div style={{ minWidth: 200 }}>
            <div style={{ marginBottom: 4 }}>Mês/Ano</div>
            <DatePicker
              style={{ width: '100%' }}
              picker="month"
              value={filters.mesAno}
              onChange={(value) => handleFilterChange('mesAno', value)}
              placeholder="Selecione o mês"
              format="MM/YYYY"
            />
          </div>
          <div style={{ minWidth: 240 }}>
            <div style={{ marginBottom: 4 }}>Busca (matrícula, aluno ou responsável)</div>
            <Input
              placeholder="Digite matrícula, aluno ou responsável"
              value={filters.busca}
              allowClear
              prefix={<SearchOutlined />}
              onChange={(e) => handleFilterChange('busca', e.target.value)}
              onPressEnter={handleApplyFilters}
            />
          </div>
        </Space>
      </Card>

      <Card title="Parcelas">
        <Table
          rowKey="id"
          dataSource={parcelas}
          columns={columns}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={onTableChange}
        />
      </Card>
    </Space>
  );
};

export default ParcelasList;
